import { useEffect, useState, type FormEvent } from "react";
import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { EmptyState, SkeletonTable, askPermanent } from "../components/Ui";
import { HowItWorks } from "../components/HowItWorks";
import { Field } from "../components/Field";

interface Programme {
  id: string;
  code: string;
  name: string;
}

interface Session {
  id: string;
  code: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  programme: Programme;
  _count: { batches: number };
}

interface Batch {
  id: string;
  name: string;
  deliveryPattern: string;
  academicSession: {
    id: string;
    code: string;
    name: string;
    status: string;
    programme: Programme;
  };
  _count: { sections: number };
}

const STATUSES = ["PLANNED", "ACTIVE", "COMPLETED", "CANCELLED"] as const;

/** A term's dates, without the time nobody set. */
const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

/**
 * Academic structure — programmes, terms and batches (FR-CRS-005).
 *
 * THIS PAGE EXISTS BECAUSE THE ONES BELOW IT COULD NOT BE USED. Creating a
 * section requires a batchId; creating a batch requires an academicSessionId;
 * and until now nothing in the running system produced either. Both were seeded
 * once and never again, so `POST /sections` was a door with no handle on this
 * side — reachable, correctly guarded, and impossible to call.
 *
 * The order on screen is the order of the dependency: a term holds intakes, an
 * intake holds batches. (The models are still called Batch and Section — only
 * the words on screen moved, see navigation.ts.) Choosing a term filters the
 * intakes beneath it, because
 * "which term is this batch in" is the question somebody gets wrong at the start
 * of every year.
 */
export function StructurePage() {
  const { hasRole } = useAuth();
  const mayEdit = hasRole("super_admin", "admin");

  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Which term the batch panel is filtered to. "" is all of them. */
  const [termFilter, setTermFilter] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const [newSession, setNewSession] = useState({
    programmeId: "",
    name: "",
    code: "",
    startDate: "",
    endDate: "",
  });
  const [newBatch, setNewBatch] = useState({
    academicSessionId: "",
    name: "",
    deliveryPattern: "",
  });

  async function load() {
    setError(null);
    try {
      const [s, b] = await Promise.all([
        api.get<Session[]>("/academic-sessions"),
        api.get<Batch[]>("/batches"),
      ]);
      setSessions(s);
      setBatches(b);
    } catch (e) {
      setError(e instanceof ApiError ? e : null);
    }
  }

  useEffect(() => {
    void load();
    // A teacher may read the structure but holds no programme:create, and the
    // picker is only used for creating. If this is refused the form is simply
    // not offered.
    api
      .get<Programme[]>("/programmes")
      .then(setProgrammes)
      .catch(() => setProgrammes([]));
  }, []);

  /** One place, so every form reports a refusal the same way. */
  async function run(work: () => Promise<unknown>, said: string) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      setNote(said);
      await load();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e : null);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function createSession(e: FormEvent) {
    e.preventDefault();
    const ok = await run(
      () => api.post("/academic-sessions", newSession),
      `Term ${newSession.code} created.`,
    );
    if (ok) setNewSession({ programmeId: "", name: "", code: "", startDate: "", endDate: "" });
  }

  async function createBatch(e: FormEvent) {
    e.preventDefault();
    const ok = await run(() => api.post("/batches", newBatch), `Intake "${newBatch.name}" created.`);
    if (ok) setNewBatch({ academicSessionId: "", name: "", deliveryPattern: "" });
  }

  async function saveSession(id: string) {
    const ok = await run(
      () =>
        api.patch(`/academic-sessions/${id}`, {
          ...(draft["name"] ? { name: draft["name"] } : {}),
          ...(draft["status"] ? { status: draft["status"] } : {}),
          ...(draft["startDate"] ? { startDate: draft["startDate"] } : {}),
          ...(draft["endDate"] ? { endDate: draft["endDate"] } : {}),
        }),
      "Term updated.",
    );
    if (ok) {
      setEditing(null);
      setDraft({});
    }
  }

  /**
   * Remove a batch created by mistake.
   *
   * Refused while it holds any section, and the refusal names them. A batch
   * looks empty from this screen — the students and the marks are a level
   * further down, in the sections — so "it has nothing in it" is exactly the
   * judgement an administrator cannot safely make from here, and the server
   * makes it instead.
   */
  async function removeBatch(b: Batch) {
    if (
      !window.confirm(
        `Delete the intake "${b.name}"?

Only possible while it holds no batches. ` +
          `It leaves every list and every dropdown.`,
      )
    )
      return;
    const forever = askPermanent(`the intake "${b.name}"`);
    await run(
      () => api.del(`/batches/${b.id}${forever ? "/permanent" : ""}`),
      forever ? `Intake "${b.name}" erased permanently.` : `Intake "${b.name}" deleted.`,
    );
  }

  async function saveBatch(id: string) {
    const ok = await run(
      () =>
        api.patch(`/batches/${id}`, {
          ...(draft["name"] ? { name: draft["name"] } : {}),
          ...(draft["deliveryPattern"] ? { deliveryPattern: draft["deliveryPattern"] } : {}),
        }),
      "Intake updated.",
    );
    if (ok) {
      setEditing(null);
      setDraft({});
    }
  }

  if (!sessions) return <SkeletonTable rows={6} columns={4} />;

  const visibleBatches = termFilter
    ? batches.filter((b) => b.academicSession.id === termFilter)
    : batches;

  /* A term that has ended cannot take new batches, so it is not offered. */
  const openSessions = sessions.filter(
    (s) => s.status !== "COMPLETED" && s.status !== "CANCELLED",
  );

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Academic structure</h1>
          <p className="muted small">
            Terms hold intakes; intakes hold batches. Set these up before teaching starts.
          </p>
        </div>
        <span className="muted small">
          {sessions.length} {sessions.length === 1 ? "term" : "terms"} · {batches.length}{" "}
          {batches.length === 1 ? "intake" : "intakes"}
        </span>
      </header>

      <HowItWorks
        id="structure"
        title="Terms and intakes"
        intro="The calendar the whole Institute runs on. Set once a term, then left alone."
        steps={[
          { icon: "calendar", title: "Make the session", body: "The term itself — its name and the dates it runs between." },
          { icon: "users", title: "Add the intakes", body: "A group starting together. Every batch belongs to one." },
          { icon: "clock", title: "Set the dates", body: "Everything else — deadlines, attendance, certificates — is measured against these." },
          { icon: "check", title: "Make it current", body: "The current term is the one new batches and applications default to." },
        ]}
        note="Dates here are the ones everything else is judged against, so a term with the wrong end date makes every completion figure wrong at once."
      />

      {error && (
        <div className="alert alert-error" role="alert">
          <strong>That did not work</strong>
          <p>{error.details?.map((d) => d.message).join(" ") ?? error.message}</p>
          {error.reference && <p className="muted small">Reference: {error.reference}</p>}
        </div>
      )}
      {note && (
        <div className="alert alert-ok" role="status">
          <p>{note}</p>
        </div>
      )}

      {/* ------------------------------------------------------------ terms */}
      <section className="card">
        <h2>Terms</h2>
        {sessions.length === 0 ? (
          <EmptyState
            title="No terms yet"
          >
            A term is an intake — Spring 2026, Fall 2026. Everything else hangs off one.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Programme</th>
                  <th>Runs</th>
                  <th className="num">Intakes</th>
                  <th>Status</th>
                  {mayEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const isEditing = editing === `s:${s.id}`;
                  return (
                    <tr key={s.id}>
                      <td>
                        <code>{s.code}</code>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            aria-label="Term name"
                            value={draft["name"] ?? s.name}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                          />
                        ) : (
                          s.name
                        )}
                      </td>
                      <td className="muted small">{s.programme.code}</td>
                      <td className="muted small">
                        {isEditing ? (
                          <span className="date-pair">
                            <input
                              type="date"
                              aria-label="Start date"
                              value={draft["startDate"] ?? day(s.startDate)}
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, startDate: e.target.value }))
                              }
                            />
                            <input
                              type="date"
                              aria-label="End date"
                              value={draft["endDate"] ?? day(s.endDate)}
                              onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
                            />
                          </span>
                        ) : (
                          `${day(s.startDate)} → ${day(s.endDate)}`
                        )}
                      </td>
                      <td className="num">{s._count.batches}</td>
                      <td>
                        {isEditing ? (
                          <select
                            aria-label="Term status"
                            value={draft["status"] ?? s.status}
                            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                          >
                            {STATUSES.map((x) => (
                              <option key={x} value={x}>
                                {x.toLowerCase()}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`pill ${s.status === "ACTIVE" ? "pill-ok" : ""}`}>
                            {s.status.toLowerCase()}
                          </span>
                        )}
                      </td>
                      {mayEdit && (
                        <td className="row-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={busy}
                                onClick={() => void saveSession(s.id)}
                              >
                                Save
                              </button>
                              <button
                                className="btn btn-quiet btn-sm"
                                onClick={() => {
                                  setEditing(null);
                                  setDraft({});
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-quiet btn-sm"
                              onClick={() => {
                                setEditing(`s:${s.id}`);
                                setDraft({});
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {mayEdit && (
          <form className="inline-form" onSubmit={(e) => void createSession(e)}>
            <h3>Add a term</h3>
            <div className="form-row">
              <Field label="Programme" required><select
                  required
                  value={newSession.programmeId}
                  onChange={(e) => setNewSession((s) => ({ ...s, programmeId: e.target.value }))}
                >
                  <option value="">Choose one</option>
                  {programmes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Code" required hint={<>Used in registration numbers — permanent.</>}><input
                  required
                  maxLength={10}
                  placeholder="SP26"
                  value={newSession.code}
                  onChange={(e) =>
                    setNewSession((s) => ({ ...s, code: e.target.value.toUpperCase() }))
                  }
                />
                {/* Said here because it cannot be changed afterwards. */}
              </Field>
              <Field label="Name" required><input
                  required
                  minLength={3}
                  placeholder="Spring 2026"
                  value={newSession.name}
                  onChange={(e) => setNewSession((s) => ({ ...s, name: e.target.value }))}
                />
              </Field>
              <Field label="Starts" required><input
                  type="date"
                  required
                  value={newSession.startDate}
                  onChange={(e) => setNewSession((s) => ({ ...s, startDate: e.target.value }))}
                />
              </Field>
              <Field label="Ends" required><input
                  type="date"
                  required
                  value={newSession.endDate}
                  onChange={(e) => setNewSession((s) => ({ ...s, endDate: e.target.value }))}
                />
              </Field>
            </div>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Working…" : "Create term"}
            </button>
            {programmes.length === 0 && (
              <p className="warn small">
                No programme is available to you, so a term cannot be created here.
              </p>
            )}
          </form>
        )}
      </section>

      {/* ---------------------------------------------------------- batches */}
      <section className="card">
        <div className="card-head">
          <h2>Intakes</h2>
          <label className="field field-inline">
            <span>Term</span>
            <select value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
              <option value="">All terms</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {visibleBatches.length === 0 ? (
          <EmptyState
            title={termFilter ? "No intakes in that term" : "No intakes yet"}
          >
            An intake groups the batches that run together — one delivery pattern, one start.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Pattern</th>
                  <th>Term</th>
                  <th className="num">Batches</th>
                  {mayEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {visibleBatches.map((b) => {
                  const isEditing = editing === `b:${b.id}`;
                  return (
                    <tr key={b.id}>
                      <td>
                        {isEditing ? (
                          <input
                            aria-label="Intake name"
                            value={draft["name"] ?? b.name}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                          />
                        ) : (
                          b.name
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            aria-label="Delivery pattern"
                            value={draft["deliveryPattern"] ?? b.deliveryPattern}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, deliveryPattern: e.target.value }))
                            }
                          />
                        ) : (
                          <span className="pill">{b.deliveryPattern}</span>
                        )}
                      </td>
                      <td className="muted small">
                        {b.academicSession.programme.code} · {b.academicSession.code}
                      </td>
                      <td className="num">{b._count.sections}</td>
                      {mayEdit && (
                        <td className="row-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={busy}
                                onClick={() => void saveBatch(b.id)}
                              >
                                Save
                              </button>
                              <button
                                className="btn btn-quiet btn-sm"
                                onClick={() => {
                                  setEditing(null);
                                  setDraft({});
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-quiet btn-sm"
                                onClick={() => {
                                  setEditing(`b:${b.id}`);
                                  setDraft({});
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-quiet btn-sm"
                                disabled={busy}
                                onClick={() => void removeBatch(b)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {mayEdit && (
          <form className="inline-form" onSubmit={(e) => void createBatch(e)}>
            <h3>Add an intake</h3>
            <div className="form-row">
              <Field label="Term"><select
                  required
                  value={newBatch.academicSessionId}
                  onChange={(e) =>
                    setNewBatch((b) => ({ ...b, academicSessionId: e.target.value }))
                  }
                >
                  <option value="">Choose one</option>
                  {openSessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.programme.code} · {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Name"><input
                  required
                  minLength={3}
                  placeholder="Morning intake"
                  value={newBatch.name}
                  onChange={(e) => setNewBatch((b) => ({ ...b, name: e.target.value }))}
                />
              </Field>
              <Field label="Delivery pattern"><input
                  required
                  minLength={2}
                  placeholder="Weekday"
                  value={newBatch.deliveryPattern}
                  onChange={(e) => setNewBatch((b) => ({ ...b, deliveryPattern: e.target.value }))}
                />
              </Field>
            </div>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Working…" : "Create intake"}
            </button>
            {/* Named rather than left as an empty list somebody stares at. */}
            {openSessions.length === 0 && sessions.length > 0 && (
              <p className="warn small">
                Every term is completed or cancelled, so a new batch cannot be added to any of
                them. Add a term first.
              </p>
            )}
          </form>
        )}
      </section>
    </>
  );
}
